'use client';

import Button from '@src/v3/components/coreUI/button';
import {
  BATHROOM_OPTIONS,
  BEDROOM_OPTIONS,
} from '@src/v3/constants/listingsFilter';
import { RoomsDropdownProps } from '@src/v3/interfaces/components/coreUI/filterDropdown/roomsDropdown';
import clsx from 'clsx';
import React from 'react';

const RoomsDropdown: React.FC<RoomsDropdownProps> = ({
  selectedBeds = [],
  selectedBathrooms = [],
  onBedsChange,
  onBathroomsChange,
  containerClassName = '',
}) => {
  const handleBedToggle = (bed: string) => {
    const currentBeds = selectedBeds || [];
    if (currentBeds.includes(bed)) {
      // Remove if already selected
      onBedsChange?.(currentBeds.filter((b) => b !== bed));
    } else {
      // Add if not selected
      onBedsChange?.([...currentBeds, bed]);
    }
  };

  const handleBathroomToggle = (bathroom: string) => {
    const currentBathrooms = selectedBathrooms || [];
    if (currentBathrooms.includes(bathroom)) {
      // Remove if already selected
      onBathroomsChange?.(currentBathrooms.filter((b) => b !== bathroom));
    } else {
      // Add if not selected
      onBathroomsChange?.([...currentBathrooms, bathroom]);
    }
  };

  const isSelected = (value: string, type: 'bed' | 'bathroom') => {
    return type === 'bed'
      ? (selectedBeds || []).includes(value)
      : (selectedBathrooms || []).includes(value);
  };

  return (
    <div className={containerClassName}>
      <div className="flex flex-col gap-y-6">
        {/* Header */}
        <div className="flex flex-col">
          <span className="text-xl font-medium text-black-dark">
            Beds & Bathroom
          </span>
        </div>

        <div className="flex flex-col gap-y-4">
          {/* Beds Section */}
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-1">
              <span className="text-xl font-medium text-black-dark">Beds</span>
              <span className="text-base text-gray-dark">
                Select all that apply.
              </span>
            </div>

            <div className="flex gap-1 flex-wrap">
              {BEDROOM_OPTIONS.map(({ key, value }) => (
                <Button
                  key={`bedroom-${key}`}
                  variant="outline"
                  size="md"
                  onClick={() => handleBedToggle(key)}
                  className={clsx(
                    isSelected(key, 'bed')
                      ? 'border-primary-dark  bg-primary-lighter text-black-dark hover:bg-primary-lighter/50'
                      : 'border-gray-light bg-white text-gray-dark hover:bg-gray-light hover:text-gray-dark hover:border-gray-light',
                    'shadow-none w-[72px] py-1 rounded-xl text-lg tracking-[-0.18px]'
                  )}
                >
                  {value}
                </Button>
              ))}
            </div>
          </div>

          {/* Bathrooms Section */}
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-1">
              <span className="text-xl font-medium text-black-dark">
                Bathrooms
              </span>
              <span className="text-base text-gray-dark">
                Select all that apply.
              </span>
            </div>

            <div className="flex gap-1 flex-wrap">
              {BATHROOM_OPTIONS.map(({ key, value }) => (
                <Button
                  key={`bathroom-${key}`}
                  variant="outline"
                  size="md"
                  onClick={() => handleBathroomToggle(key)}
                  className={clsx(
                    isSelected(key, 'bathroom')
                      ? 'border-primary-dark  bg-primary-lighter text-black-dark hover:bg-primary-lighter/50'
                      : 'border-gray-light bg-white text-gray-dark hover:bg-gray-light hover:text-gray-dark hover:border-gray-light',
                    'shadow-none w-[72px] py-1 rounded-xl text-lg tracking-[-0.18px]'
                  )}
                >
                  {value}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomsDropdown;
